import { useState, useEffect } from "react";
import { Camera, Calendar, Upload, User, GraduationCap, Star, MessageSquare, LogOut, Users, Loader2, History, BarChart3, Image as ImageIcon } from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Separator } from "@/components/ui/separator";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { PhotoUploader } from "@/components/PhotoUploader";
import { ProgressTracker } from "@/components/ProgressTracker";
import { AssignmentHistory } from "@/components/AssignmentHistory";
import { TeacherStats } from "@/components/TeacherStats";
import { PhotoViewer } from "@/components/PhotoViewer";
import { ThemeToggle } from "@/components/ThemeToggle";
import { trackEvent, identifyUser, Events } from "@/lib/analytics";

type Course = {
  id: string;
  day: number;
  weekday: string;
  theme: string;
  assignment: string;
};

type Student = {
  id: string;
  name: string;
  avatar_url: string | null;
};

type Teacher = {
  id: string;
  teacher_id: string;
  name: string;
};

type Assignment = {
  id: string;
  student_id: string;
  course_day: number;
  photos: string[];
  description: string | null;
  submitted_at: string;
  score: number | null;
  comment: string | null;
  reviewed_by: string | null;
  views?: number;
  student?: Student;
};

type UserRole = "student" | "teacher" | null;

const Index = () => {
  const { toast } = useToast();
  const [currentDay, setCurrentDay] = useState(1);
  const [userRole, setUserRole] = useState<UserRole>(null);
  const [userName, setUserName] = useState("");
  const [userAvatar, setUserAvatar] = useState("");
  const [teacherId, setTeacherId] = useState("");
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [selectedPhotos, setSelectedPhotos] = useState<File[]>([]);
  const [photoPreview, setPhotoPreview] = useState<string[]>([]);
  const [description, setDescription] = useState("");
  const [courses, setCourses] = useState<Course[]>([]);
  const [assignments, setAssignments] = useState<Assignment[]>([]);
  const [studentAssignments, setStudentAssignments] = useState<Assignment[]>([]);
  const [selectedAssignment, setSelectedAssignment] = useState<Assignment | null>(null);
  const [commentText, setCommentText] = useState("");
  const [scoreValue, setScoreValue] = useState("");
  const [loading, setLoading] = useState(false);
  const [currentStudent, setCurrentStudent] = useState<Student | null>(null);
  const [currentTeacher, setCurrentTeacher] = useState<Teacher | null>(null);
  const [photoViewerOpen, setPhotoViewerOpen] = useState(false);
  const [viewerPhotos, setViewerPhotos] = useState<string[]>([]);
  const [viewerIndex, setViewerIndex] = useState(0);
  const [activeTab, setActiveTab] = useState("submit");

  const currentCourse = courses.find(c => c.day === currentDay);

  const loadCourses = async () => {
    const { data, error } = await supabase
      .from("courses")
      .select("*")
      .order("day", { ascending: true });

    if (error) {
      toast({ title: "加载失败", description: error.message, variant: "destructive" });
    } else if (data) {
      setCourses(data);
    }
  };

  const loadAssignments = async () => {
    const { data, error } = await supabase
      .from("assignment_submissions")
      .select(`
        *,
        student:students(*)
      `)
      .order("submitted_at", { ascending: false });

    if (error) {
      toast({ title: "加载失败", description: error.message, variant: "destructive" });
    } else if (data) {
      setAssignments(data as Assignment[]);
    }
  };

  const loadStudentAssignments = async (studentId: string) => {
    const { data, error } = await supabase
      .from("assignment_submissions")
      .select("*")
      .eq("student_id", studentId)
      .order("course_day", { ascending: true });

    if (error) {
      toast({ title: "加载失败", description: error.message, variant: "destructive" });
    } else if (data) {
      setStudentAssignments(data as Assignment[]);
    }
  };

  // 加载课程数据
  useEffect(() => {
    void loadCourses();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 加载作业数据（教师视图）
  useEffect(() => {
    if (userRole === "teacher" && isLoggedIn) {
      void loadAssignments();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userRole, isLoggedIn]);

  // 加载学员作业历史
  useEffect(() => {
    if (userRole === "student" && isLoggedIn && currentStudent) {
      void loadStudentAssignments(currentStudent.id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userRole, isLoggedIn, currentStudent]);

  const handleAvatarUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setUserAvatar(reader.result as string);
      };
      reader.readAsDataURL(file);

      // 上传到 Supabase Storage
      const fileExt = file.name.split(".").pop();
      const fileName = `${Math.random().toString(36).substr(2, 9)}.${fileExt}`;
      const { error } = await supabase.storage
        .from("photos")
        .upload(`avatars/${fileName}`, file);

      if (error) {
        console.error("头像上传失败:", error);
      } else {
        const { data: { publicUrl } } = supabase.storage
          .from("photos")
          .getPublicUrl(`avatars/${fileName}`);
        setUserAvatar(publicUrl);
      }
    }
  };

  const handlePhotosChange = (files: File[], previews: string[]) => {
    setSelectedPhotos(files);
    setPhotoPreview(previews);
  };

  const handleStudentLogin = async () => {
    if (!userName.trim()) {
      toast({ title: "提示", description: "请输入姓名", variant: "destructive" });
      return;
    }

    setLoading(true);
    try {
      // 创建或获取学员信息
      const { data, error } = await supabase
        .from("students")
        .insert([{ name: userName, avatar_url: userAvatar }])
        .select()
        .single();

      if (error) throw error;

      setCurrentStudent(data);
      setIsLoggedIn(true);
      
      // PostHog 事件追踪 - 学员登录
      identifyUser(data.id, {
        name: data.name,
        user_type: 'student',
        created_at: data.created_at,
      });
      trackEvent(Events.STUDENT_LOGIN, {
        student_id: data.id,
        student_name: data.name,
      });
      
      toast({ title: "登录成功", description: `欢迎，${userName}！` });
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : "登录失败";
      toast({ title: "登录失败", description: errorMessage, variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  const handleTeacherLogin = async () => {
    if (!teacherId.trim()) {
      toast({ title: "提示", description: "请输入教师ID", variant: "destructive" });
      return;
    }

    setLoading(true);
    try {
      // 查找或创建教师
      const { data, error } = await supabase
        .from("teachers")
        .select("*")
        .eq("teacher_id", teacherId)
        .maybeSingle();

      if (error) throw error;

      let teacherData = data;

      if (!teacherData) {
        // 创建新教师
        const { data: newTeacher, error: insertError } = await supabase
          .from("teachers")
          .insert([{ teacher_id: teacherId, name: `教师_${teacherId}` }])
          .select()
          .single();

        if (insertError) throw insertError;
        teacherData = newTeacher;
      }

      setCurrentTeacher(teacherData);
      setIsLoggedIn(true);
      
      // PostHog 事件追踪 - 教师登录
      identifyUser(teacherData.id, {
        name: teacherData.name,
        user_type: 'teacher',
        teacher_id: teacherData.teacher_id,
      });
      trackEvent(Events.TEACHER_LOGIN, {
        teacher_id: teacherData.teacher_id,
        teacher_name: teacherData.name,
      });
      
      toast({ title: "登录成功", description: `欢迎，${teacherData.name}！` });
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : "登录失败";
      toast({ title: "登录失败", description: errorMessage, variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    if (selectedPhotos.length === 0) {
      toast({ title: "提示", description: "请至少上传一张照片", variant: "destructive" });
      return;
    }

    if (!currentStudent) {
      toast({ title: "错误", description: "未找到学员信息", variant: "destructive" });
      return;
    }

    setLoading(true);
    try {
      // 上传照片到 Storage
      const photoUrls: string[] = [];
      for (const file of selectedPhotos) {
        const fileExt = file.name.split(".").pop();
        const fileName = `${currentStudent.id}_${Date.now()}_${Math.random().toString(36).substr(2, 9)}.${fileExt}`;
        
        const { error: uploadError } = await supabase.storage
          .from("photos")
          .upload(`assignments/${fileName}`, file);

        if (uploadError) throw uploadError;

        const { data: { publicUrl } } = supabase.storage
          .from("photos")
          .getPublicUrl(`assignments/${fileName}`);
        
        photoUrls.push(publicUrl);
      }

      // 保存作业到数据库
      const { error: insertError } = await supabase
        .from("assignment_submissions")
        .insert([{
          student_id: currentStudent.id,
          course_day: currentDay,
          photos: photoUrls,
          description: description
        }]);

      if (insertError) throw insertError;

      // 更新学员进度
      await supabase
        .from("student_progress")
        .upsert({
          student_id: currentStudent.id,
          course_day: currentDay,
          completed: true,
          completed_at: new Date().toISOString()
        });

      toast({ title: "提交成功", description: "您的作业已成功提交！" });
      
      // PostHog 事件追踪 - 作业提交（关键事件）
      trackEvent(Events.ASSIGNMENT_SUBMIT, {
        student_id: currentStudent.id,
        student_name: currentStudent.name,
        course_day: currentDay,
        course_theme: currentCourse?.theme || 'unknown',
        photo_count: selectedPhotos.length,
        has_description: description.length > 0,
      });
      
      setSelectedPhotos([]);
      setPhotoPreview([]);
      setDescription("");
      
      // 重新加载学员作业
      loadStudentAssignments(currentStudent.id);
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : "提交失败";
      toast({ title: "提交失败", description: errorMessage, variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    setIsLoggedIn(false);
    setUserRole(null);
    setUserName("");
    setTeacherId("");
    setUserAvatar("");
    setCurrentStudent(null);
    setCurrentTeacher(null);
  };

  const handleReviewAssignment = (assignment: Assignment) => {
    setSelectedAssignment(assignment);
    setCommentText(assignment.comment || "");
    setScoreValue(assignment.score?.toString() || "");
  };

  const handleSaveReview = async () => {
    if (!selectedAssignment || !currentTeacher) return;

    const score = parseInt(scoreValue);
    if (isNaN(score) || score < 0 || score > 100) {
      toast({ title: "提示", description: "请输入0-100之间的分数", variant: "destructive" });
      return;
    }

    setLoading(true);
    try {
      const { error } = await supabase
        .from("assignment_submissions")
        .update({
          score,
          comment: commentText,
          reviewed_by: currentTeacher.id,
          reviewed_at: new Date().toISOString()
        })
        .eq("id", selectedAssignment.id);

      if (error) throw error;

      toast({ title: "保存成功", description: "点评已保存！" });
      
      // PostHog 事件追踪 - 作业点评
      trackEvent(Events.ASSIGNMENT_REVIEW, {
        teacher_id: currentTeacher.id,
        teacher_name: currentTeacher.name,
        assignment_id: selectedAssignment.id,
        course_day: selectedAssignment.course_day,
        score: score,
        has_comment: commentText.length > 0,
      });
      
      setSelectedAssignment(null);
      setCommentText("");
      setScoreValue("");
      loadAssignments();
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : "保存失败";
      toast({ title: "保存失败", description: errorMessage, variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  const openPhotoViewer = (photos: string[], index: number = 0) => {
    setViewerPhotos(photos);
    setViewerIndex(index);
    setPhotoViewerOpen(true);
  };

  const calculateTeacherStats = () => {
    const uniqueStudents = new Set(assignments.map(a => a.student_id)).size;
    const gradedAssignments = assignments.filter(a => a.score !== null);
    const averageScore = gradedAssignments.length > 0
      ? gradedAssignments.reduce((sum, a) => sum + (a.score || 0), 0) / gradedAssignments.length
      : 0;
    const completionRate = (gradedAssignments.length / Math.max(assignments.length, 1)) * 100;

    return {
      totalStudents: uniqueStudents,
      totalSubmissions: assignments.length,
      averageScore,
      completionRate
    };
  };

  const getProgressCourses = () => {
    return courses.map(course => ({
      day: course.day,
      theme: course.theme,
      completed: studentAssignments.some(a => a.course_day === course.day)
    }));
  };

  // 角色选择界面
  if (!userRole) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-background via-accent/30 to-background flex items-center justify-center p-4">
        <div className="absolute top-4 right-4">
          <ThemeToggle />
        </div>
        
        <div className="w-full max-w-4xl">
          <div className="mb-8 text-center">
            <div className="inline-flex items-center gap-2 mb-2">
              <Camera className="h-8 w-8 text-primary" />
              <h1 className="text-3xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-primary to-purple-600">
                摄影班作业提交系统
              </h1>
            </div>
            <p className="text-muted-foreground">21天摄影学习之旅 · 记录成长点滴</p>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            <Card className="shadow-[var(--shadow-medium)] hover:shadow-[var(--shadow-medium)] hover:scale-105 transition-all cursor-pointer"
                  onClick={() => setUserRole("student")}>
              <CardHeader className="text-center">
                <div className="mx-auto mb-4 h-16 w-16 rounded-full bg-primary/10 flex items-center justify-center">
                  <User className="h-8 w-8 text-primary" />
                </div>
                <CardTitle>学员入口</CardTitle>
                <CardDescription>提交作业，记录成长</CardDescription>
              </CardHeader>
              <CardContent>
                <Button className="w-full" variant="outline">
                  我是学员
                </Button>
              </CardContent>
            </Card>

            <Card className="shadow-[var(--shadow-medium)] hover:shadow-[var(--shadow-medium)] hover:scale-105 transition-all cursor-pointer"
                  onClick={() => setUserRole("teacher")}>
              <CardHeader className="text-center">
                <div className="mx-auto mb-4 h-16 w-16 rounded-full bg-primary/10 flex items-center justify-center">
                  <GraduationCap className="h-8 w-8 text-primary" />
                </div>
                <CardTitle>教师入口</CardTitle>
                <CardDescription>批改作业，指导学员</CardDescription>
              </CardHeader>
              <CardContent>
                <Button className="w-full" variant="outline">
                  我是老师
                </Button>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    );
  }

  // 学员登录界面
  if (userRole === "student" && !isLoggedIn) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-background via-accent/30 to-background">
        <div className="absolute top-4 right-4">
          <ThemeToggle />
        </div>
        
        <div className="container mx-auto px-4 py-8 max-w-2xl">
          <div className="mb-8">
            <Button variant="ghost" onClick={() => setUserRole(null)}>
              返回
            </Button>
          </div>

          <div className="mb-8 text-center">
            <div className="inline-flex items-center gap-2 mb-2">
              <Camera className="h-8 w-8 text-primary" />
              <h1 className="text-3xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-primary to-purple-600">
                学员登录
              </h1>
            </div>
            <p className="text-muted-foreground">记录您的摄影学习之旅</p>
          </div>

          <Card className="shadow-[var(--shadow-medium)]">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <User className="h-5 w-5" />
                个人信息
              </CardTitle>
              <CardDescription>请输入您的姓名并上传头像</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex flex-col sm:flex-row gap-4 items-center">
                <div className="flex flex-col items-center gap-2">
                  <Avatar className="h-20 w-20">
                    <AvatarImage src={userAvatar} />
                    <AvatarFallback className="bg-primary/10 text-primary">
                      <Camera className="h-8 w-8" />
                    </AvatarFallback>
                  </Avatar>
                  <Label htmlFor="avatar" className="cursor-pointer text-sm text-primary hover:underline">
                    上传头像
                  </Label>
                  <Input
                    id="avatar"
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={handleAvatarUpload}
                  />
                </div>
                <div className="flex-1 w-full space-y-3">
                  <div>
                    <Label htmlFor="username">姓名</Label>
                    <Input
                      id="username"
                      placeholder="请输入您的姓名"
                      value={userName}
                      onChange={(e) => setUserName(e.target.value)}
                    />
                  </div>
                  <Button onClick={handleStudentLogin} className="w-full" disabled={loading}>
                    {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                    开始学习
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  // 教师登录界面
  if (userRole === "teacher" && !isLoggedIn) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-background via-accent/30 to-background">
        <div className="absolute top-4 right-4">
          <ThemeToggle />
        </div>
        
        <div className="container mx-auto px-4 py-8 max-w-2xl">
          <div className="mb-8">
            <Button variant="ghost" onClick={() => setUserRole(null)}>
              返回
            </Button>
          </div>

          <div className="mb-8 text-center">
            <div className="inline-flex items-center gap-2 mb-2">
              <GraduationCap className="h-8 w-8 text-primary" />
              <h1 className="text-3xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-primary to-purple-600">
                教师登录
              </h1>
            </div>
            <p className="text-muted-foreground">批改作业，指导学员成长</p>
          </div>

          <Card className="shadow-[var(--shadow-medium)]">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <GraduationCap className="h-5 w-5" />
                教师身份验证
              </CardTitle>
              <CardDescription>请输入您的教师ID</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label htmlFor="teacher-id">教师ID</Label>
                <Input
                  id="teacher-id"
                  placeholder="请输入教师ID（例如：TEACHER001）"
                  value={teacherId}
                  onChange={(e) => setTeacherId(e.target.value)}
                />
              </div>
              <Button onClick={handleTeacherLogin} className="w-full" disabled={loading}>
                {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                登录
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  // 学员主界面
  if (userRole === "student" && isLoggedIn && currentStudent) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-background via-accent/30 to-background">
        <div className="container mx-auto px-4 py-8 max-w-7xl">
          <div className="mb-8 flex items-center justify-between">
            <div className="inline-flex items-center gap-2">
              <Camera className="h-8 w-8 text-primary" />
              <h1 className="text-3xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-primary to-purple-600">
                摄影班作业提交系统
              </h1>
            </div>
            <div className="flex items-center gap-2">
              <ThemeToggle />
              <Button variant="ghost" size="sm" onClick={handleLogout}>
                <LogOut className="h-4 w-4" />
              </Button>
            </div>
          </div>

          <div className="mb-8 flex items-center justify-between p-4 bg-card rounded-lg border shadow-[var(--shadow-soft)]">
            <div className="flex items-center gap-3">
              <Avatar className="h-12 w-12 border-2 border-primary">
                <AvatarImage src={currentStudent.avatar_url || ""} />
                <AvatarFallback className="bg-primary text-primary-foreground">
                  {currentStudent.name.charAt(0)}
                </AvatarFallback>
              </Avatar>
              <div>
                <p className="font-medium">{currentStudent.name}</p>
                <p className="text-sm text-muted-foreground">学员ID: {currentStudent.id.slice(0, 8).toUpperCase()}</p>
              </div>
            </div>
            <Badge variant="secondary" className="text-xs">
              第 {currentDay}/21 天
            </Badge>
          </div>

          <div className="grid lg:grid-cols-3 gap-6 mb-6">
            <div className="lg:col-span-2 space-y-6">
              {currentCourse && (
                <>
                  <Card className="shadow-[var(--shadow-medium)] border-l-4 border-l-primary">
                    <CardHeader>
                      <div className="flex items-center justify-between">
                        <CardTitle className="flex items-center gap-2">
                          <Calendar className="h-5 w-5 text-primary" />
                          今日课程
                        </CardTitle>
                        <Badge className="text-sm">{currentCourse.weekday}</Badge>
                      </div>
                      <CardDescription>第 {currentCourse.day} 课</CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      <div>
                        <h3 className="text-sm font-medium text-muted-foreground mb-1">学习主题</h3>
                        <p className="text-2xl font-semibold text-primary">{currentCourse.theme}</p>
                      </div>
                      <div className="bg-accent/50 p-4 rounded-lg">
                        <h3 className="text-sm font-medium text-muted-foreground mb-2">今日作业</h3>
                        <p className="text-foreground leading-relaxed whitespace-pre-line">{currentCourse.assignment}</p>
                      </div>
                    </CardContent>
                  </Card>

                  <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
                    <TabsList className="grid w-full grid-cols-2">
                      <TabsTrigger value="submit">
                        <Upload className="h-4 w-4 mr-2" />
                        提交作业
                      </TabsTrigger>
                      <TabsTrigger value="history">
                        <History className="h-4 w-4 mr-2" />
                        提交历史
                      </TabsTrigger>
                    </TabsList>

                    <TabsContent value="submit">
                      <Card className="shadow-[var(--shadow-medium)]">
                        <CardHeader>
                          <CardTitle className="flex items-center gap-2">
                            <Upload className="h-5 w-5 text-primary" />
                            提交作业
                          </CardTitle>
                          <CardDescription>上传您的摄影作品并添加描述</CardDescription>
                        </CardHeader>
                        <CardContent className="space-y-6">
                          <PhotoUploader 
                            photos={photoPreview}
                            onPhotosChange={handlePhotosChange}
                          />

                          <div>
                            <Label htmlFor="description" className="mb-2 block">
                              作业描述
                            </Label>
                            <Textarea
                              id="description"
                              placeholder="分享您的拍摄思路、拍摄参数（ISO、快门、光圈）、遇到的问题或收获..."
                              rows={4}
                              value={description}
                              onChange={(e) => setDescription(e.target.value)}
                              className="resize-none"
                            />
                          </div>

                          <Button onClick={handleSubmit} size="lg" className="w-full" disabled={loading}>
                            {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                            提交作业
                          </Button>
                        </CardContent>
                      </Card>
                    </TabsContent>

                    <TabsContent value="history">
                      <AssignmentHistory
                        assignments={studentAssignments.map(a => ({
                          ...a,
                          theme: courses.find(c => c.day === a.course_day)?.theme || "未知主题",
                          views: a.views || 0
                        }))}
                        onViewAssignment={(assignment) => {
                          if (assignment.photos.length > 0) {
                            openPhotoViewer(assignment.photos);
                          }
                        }}
                      />
                    </TabsContent>
                  </Tabs>
                </>
              )}
            </div>

            <div className="space-y-6">
              <ProgressTracker
                courses={getProgressCourses()}
                currentDay={currentDay}
                onDaySelect={setCurrentDay}
              />

              <Card className="shadow-[var(--shadow-soft)]">
                <CardHeader>
                  <CardTitle className="text-base">学习统计</CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-muted-foreground">已提交作业</span>
                    <span className="font-semibold">{studentAssignments.length}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-muted-foreground">已评分作业</span>
                    <span className="font-semibold">{studentAssignments.filter(a => a.score !== null).length}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-muted-foreground">平均分数</span>
                    <span className="font-semibold">
                      {studentAssignments.filter(a => a.score !== null).length > 0
                        ? (studentAssignments.reduce((sum, a) => sum + (a.score || 0), 0) / studentAssignments.filter(a => a.score !== null).length).toFixed(1)
                        : "-"}
                    </span>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>

          <div className="text-center text-sm text-muted-foreground">
            <p>课程共21天 · 坚持每日练习，记录成长点滴</p>
          </div>
        </div>

        {photoViewerOpen && (
          <PhotoViewer
            photos={viewerPhotos}
            initialIndex={viewerIndex}
            onClose={() => setPhotoViewerOpen(false)}
          />
        )}
      </div>
    );
  }

  // 教师主界面
  if (userRole === "teacher" && isLoggedIn && currentTeacher) {
    const stats = calculateTeacherStats();

    return (
      <div className="min-h-screen bg-gradient-to-br from-background via-accent/30 to-background">
        <div className="container mx-auto px-4 py-8 max-w-7xl">
          <div className="mb-8 flex items-center justify-between">
            <div className="inline-flex items-center gap-2">
              <GraduationCap className="h-8 w-8 text-primary" />
              <h1 className="text-3xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-primary to-purple-600">
                教师工作台
              </h1>
            </div>
            <div className="flex items-center gap-2">
              <ThemeToggle />
              <Button variant="ghost" size="sm" onClick={handleLogout}>
                <LogOut className="h-4 w-4" />
              </Button>
            </div>
          </div>

          <div className="mb-8 flex items-center justify-between p-4 bg-card rounded-lg border shadow-[var(--shadow-soft)]">
            <div className="flex items-center gap-3">
              <div className="h-12 w-12 rounded-full bg-primary/10 flex items-center justify-center">
                <GraduationCap className="h-6 w-6 text-primary" />
              </div>
              <div>
                <p className="font-medium">{currentTeacher.name}</p>
                <p className="text-sm text-muted-foreground flex items-center gap-1">
                  <Users className="h-3 w-3" />
                  共 {assignments.length} 份作业
                </p>
              </div>
            </div>
          </div>

          <div className="space-y-6">
            <TeacherStats stats={stats} />

            <Tabs defaultValue="pending" className="space-y-6">
              <TabsList className="grid w-full max-w-md mx-auto grid-cols-2">
                <TabsTrigger value="pending">
                  待批改 ({assignments.filter(a => !a.score).length})
                </TabsTrigger>
                <TabsTrigger value="reviewed">
                  已批改 ({assignments.filter(a => a.score).length})
                </TabsTrigger>
              </TabsList>

              <TabsContent value="pending" className="space-y-4">
                {assignments.filter(a => !a.score).length === 0 ? (
                  <Card>
                    <CardContent className="py-12 text-center text-muted-foreground">
                      暂无待批改作业
                    </CardContent>
                  </Card>
                ) : (
                  assignments.filter(a => !a.score).map(assignment => (
                    <Card key={assignment.id} className="shadow-[var(--shadow-soft)] hover:shadow-[var(--shadow-medium)] transition-shadow">
                      <CardHeader>
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <Avatar>
                              <AvatarImage src={assignment.student?.avatar_url || ""} />
                              <AvatarFallback className="bg-primary/10 text-primary">
                                {assignment.student?.name?.charAt(0) || "?"}
                              </AvatarFallback>
                            </Avatar>
                            <div>
                              <CardTitle className="text-lg">{assignment.student?.name || "未知学员"}</CardTitle>
                              <CardDescription>第 {assignment.course_day} 天作业 · {new Date(assignment.submitted_at).toLocaleString()}</CardDescription>
                            </div>
                          </div>
                        </div>
                      </CardHeader>
                      <CardContent className="space-y-4">
                        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                          {assignment.photos.map((photo, index) => (
                            <div 
                              key={index} 
                              className="aspect-square rounded-lg overflow-hidden border cursor-pointer hover:opacity-80 transition-opacity"
                              onClick={() => openPhotoViewer(assignment.photos, index)}
                            >
                              <img src={photo} alt={`作业照片 ${index + 1}`} className="w-full h-full object-cover" />
                            </div>
                          ))}
                        </div>
                        {assignment.description && (
                          <div className="bg-accent/50 p-4 rounded-lg">
                            <p className="text-sm text-muted-foreground mb-1">学员说：</p>
                            <p className="text-foreground whitespace-pre-line">{assignment.description}</p>
                          </div>
                        )}
                        <Button onClick={() => handleReviewAssignment(assignment)} className="w-full">
                          <MessageSquare className="h-4 w-4 mr-2" />
                          开始点评
                        </Button>
                      </CardContent>
                    </Card>
                  ))
                )}
              </TabsContent>

              <TabsContent value="reviewed" className="space-y-4">
                {assignments.filter(a => a.score).length === 0 ? (
                  <Card>
                    <CardContent className="py-12 text-center text-muted-foreground">
                      暂无已批改作业
                    </CardContent>
                  </Card>
                ) : (
                  assignments.filter(a => a.score).map(assignment => (
                    <Card key={assignment.id} className="shadow-[var(--shadow-soft)]">
                      <CardHeader>
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <Avatar>
                              <AvatarImage src={assignment.student?.avatar_url || ""} />
                              <AvatarFallback className="bg-primary/10 text-primary">
                                {assignment.student?.name?.charAt(0) || "?"}
                              </AvatarFallback>
                            </Avatar>
                            <div>
                              <CardTitle className="text-lg">{assignment.student?.name || "未知学员"}</CardTitle>
                              <CardDescription>第 {assignment.course_day} 天作业 · {new Date(assignment.submitted_at).toLocaleString()}</CardDescription>
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <Badge variant="default" className="flex items-center gap-1">
                              <Star className="h-3 w-3" />
                              {assignment.score}分
                            </Badge>
                          </div>
                        </div>
                      </CardHeader>
                      <CardContent className="space-y-4">
                        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                          {assignment.photos.map((photo, index) => (
                            <div 
                              key={index} 
                              className="aspect-square rounded-lg overflow-hidden border cursor-pointer hover:opacity-80 transition-opacity"
                              onClick={() => openPhotoViewer(assignment.photos, index)}
                            >
                              <img src={photo} alt={`作业照片 ${index + 1}`} className="w-full h-full object-cover" />
                            </div>
                          ))}
                        </div>
                        {assignment.comment && (
                          <div className="bg-primary/10 p-4 rounded-lg border-l-4 border-l-primary">
                            <p className="text-sm font-medium text-primary mb-1">教师点评：</p>
                            <p className="text-foreground whitespace-pre-line">{assignment.comment}</p>
                          </div>
                        )}
                        <Button variant="outline" onClick={() => handleReviewAssignment(assignment)} className="w-full">
                          修改点评
                        </Button>
                      </CardContent>
                    </Card>
                  ))
                )}
              </TabsContent>
            </Tabs>
          </div>

          {/* 点评弹窗 */}
          {selectedAssignment && (
            <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50" onClick={() => setSelectedAssignment(null)}>
              <Card className="w-full max-w-2xl max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <MessageSquare className="h-5 w-5" />
                    作业点评
                  </CardTitle>
                  <CardDescription>
                    {selectedAssignment.student?.name} · 第 {selectedAssignment.course_day} 天作业
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                    {selectedAssignment.photos.map((photo, index) => (
                      <div 
                        key={index} 
                        className="aspect-square rounded-lg overflow-hidden border cursor-pointer hover:opacity-80 transition-opacity"
                        onClick={() => openPhotoViewer(selectedAssignment.photos, index)}
                      >
                        <img src={photo} alt={`作业照片 ${index + 1}`} className="w-full h-full object-cover" />
                      </div>
                    ))}
                  </div>

                  {selectedAssignment.description && (
                    <div className="bg-accent/50 p-4 rounded-lg">
                      <p className="text-sm text-muted-foreground mb-1">学员说：</p>
                      <p className="text-foreground whitespace-pre-line">{selectedAssignment.description}</p>
                    </div>
                  )}

                  <Separator />

                  <div>
                    <Label htmlFor="score" className="mb-2 block">
                      评分 (0-100) *
                    </Label>
                    <Select value={scoreValue} onValueChange={setScoreValue}>
                      <SelectTrigger>
                        <SelectValue placeholder="选择分数" />
                      </SelectTrigger>
                      <SelectContent>
                        {[100, 95, 90, 85, 80, 75, 70, 65, 60].map(score => (
                          <SelectItem key={score} value={score.toString()}>
                            {score}分 {score >= 90 ? "优秀" : score >= 80 ? "良好" : score >= 70 ? "中等" : "及格"}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <Label htmlFor="comment" className="mb-2 block">
                      点评内容
                    </Label>
                    <Textarea
                      id="comment"
                      placeholder="写下您的点评和建议..."
                      rows={6}
                      value={commentText}
                      onChange={(e) => setCommentText(e.target.value)}
                      className="resize-none"
                    />
                  </div>

                  <div className="flex gap-3">
                    <Button variant="outline" onClick={() => setSelectedAssignment(null)} className="flex-1" disabled={loading}>
                      取消
                    </Button>
                    <Button onClick={handleSaveReview} className="flex-1" disabled={loading}>
                      {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                      保存点评
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </div>
          )}

          {photoViewerOpen && (
            <PhotoViewer
              photos={viewerPhotos}
              initialIndex={viewerIndex}
              onClose={() => setPhotoViewerOpen(false)}
            />
          )}
        </div>
      </div>
    );
  }

  return null;
};

export default Index;
